import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { makeBackendApi, ITravelAgent } from 'services/BackendApi';
import { IBasketBuildL4 } from 'services/BackendApi/types/Basket';
import { getLevel2BasketBuilds } from './utils';

/**
 * Hook to search basket references by pattern (autocomplete suggestions)
 */
export const useBasketReferenceSearch = (pattern: string, enabled = true) => {
  const backendApi = makeBackendApi();

  return useQuery({
    queryKey: ['basket-reference-search', pattern],
    queryFn: async (): Promise<string[]> => {
      const response = await backendApi.searchBasketReferences(pattern);
      return (response.data?.references ?? []).map(r => r.humanReadableId);
    },
    enabled: enabled,
  });
};

/**
 * Hook to fetch basket users (owners who have baskets)
 * Only runs if user is not a TA (internal users only)
 */
export const useBasketUsers = (isTa: boolean) => {
  const backendApi = makeBackendApi();

  return useQuery({
    queryKey: ['basket-users'],
    queryFn: async () => {
      const response = await backendApi.getBasketUsers();
      return response.data;
    },
    enabled: !isTa, // Only fetch if not a TA
  });
};

/**
 * Hook to fetch travel agent details including company information
 * Only runs when a valid travel agent UUID is provided
 */
export const useTravelAgent = (travelAgentUuid: string | null) => {
  const backendApi = makeBackendApi();

  return useQuery<ITravelAgent>({
    queryKey: ['basket-travel-agent', travelAgentUuid],
    queryFn: async () => {
      if (!travelAgentUuid) {
        throw new Error('Travel agent UUID is required');
      }
      const response = await backendApi.getTravelAgentViaUuid(travelAgentUuid, ['company']);
      return response.data.data;
    },
    enabled: !!travelAgentUuid,
  });
};

/**
 * Unified hook to fetch basket data (handles both Mode 1 and Mode 2)
 *
 * Mode 2 (Reference ID Search): When humanReadableId is provided
 * Mode 1 (Filter Search): When humanReadableId is NOT provided
 *
 * Includes progressive enhancement: L1 → L2 (hotel photos) → L4 (booking builder responses)
 */
export const useBasket = (params: {
  // Mode 2 parameters (optional)
  humanReadableId?: string;

  // Mode 1 parameters (optional)
  offset?: number;
  travelAgentUuid?: string | null;
  actingOnBehalfOfUserUuid?: string | null;
  isTa?: boolean;

  // Common parameters
  actingOnBehalfOfUser?: any;
  enabled?: boolean;
}) => {
  const backendApi = makeBackendApi();

  // Determine which mode we're in
  const isMode2 = !!params.humanReadableId?.trim();

  // Smart query key generation based on mode
  const queryKey = isMode2
    ? ['basket', 'byReferenceId', params.humanReadableId!.trim()]
    : [
        'basket',
        'byFilters',
        params.offset ?? 0,
        params.travelAgentUuid ?? null,
        params.actingOnBehalfOfUserUuid ?? null,
      ];

  // Smart enabled logic
  const isMode1Enabled = !!params.actingOnBehalfOfUserUuid && (params.isTa || !!params.travelAgentUuid);
  const enabled = params.enabled !== false && (isMode2 || isMode1Enabled);

  return useQuery({
    queryKey,
    queryFn: async () => {
      // L1: Fetch basket (backend handles both modes based on params)
      const response = await backendApi.getBasket({
        humanReadableId: params.humanReadableId?.trim(),
        offset: params.offset,
        travelAgentUuid: params.isTa ? undefined : params.travelAgentUuid ?? undefined,
        actingOnBehalfOfUserUuid: params.isTa ? undefined : params.actingOnBehalfOfUserUuid ?? undefined,
      });

      // L2: Enrich with hotel photos
      const buildsLevel2 = await getLevel2BasketBuilds({
        builds: response.data?.builds ?? [],
        backendApi,
      });

      // L4: Enrich with booking builder responses
      // Mode 2: Use the TA from the response (authoritative - the build's actual TA)
      // Mode 1: Use the TA from params (the selected dropdown value)
      const isMode2Search = !!params.humanReadableId?.trim();
      const effectiveTaUuid = isMode2Search
        ? response.data.travelAgentUserUuid
        : params.travelAgentUuid;

      // Skip if no TA UUID available (needed for booking builder requests)
      if (!effectiveTaUuid) {
        return {
          builds: buildsLevel2.map(b => ({ ...b, latestBookingBuilderResponse: undefined })),
          total: response.data.total,
          basketCreatorUuid: response.data.basketCreatorUuid,
          travelAgentUserUuid: response.data.travelAgentUserUuid,
        };
      }

      const responses = await Promise.allSettled(
        buildsLevel2.map(build =>
          backendApi.postBookingBuilderRequest({
            bookingBuilderRequest: build.buildRequest,
            clientCountryCode: build.clientCountryCode,
            travelAgentUuid: effectiveTaUuid,
            validateStatusFunc: status => (status >= 200 && status <= 299) || (status >= 400 && status <= 499),
            actingOnBehalfOfUser: params.actingOnBehalfOfUser ?? undefined,
            queryParams: {
              isForBookingBreakdown: true,
              basketBuildUuid: build.uuid
            },
          })
        )
      );

      const basketBuildsWithBookingBuilderResponses: IBasketBuildL4[] = buildsLevel2.map(
        (build, index): IBasketBuildL4 => {
          const response = responses[index];

          if (response && response.status === 'fulfilled' && response.value) {
            return {
              ...build,
              latestBookingBuilderResponse: (response.value.data as any).data,
            };
          } else {
            return {
              ...build,
              latestBookingBuilderResponse: undefined,
            };
          }
        }
      );

      return {
        builds: basketBuildsWithBookingBuilderResponses,
        total: response.data.total,
        basketCreatorUuid: response.data.basketCreatorUuid,
        travelAgentUserUuid: response.data.travelAgentUserUuid,
      };
    },
    enabled,
  });
};

export interface IBasketPageData {
  builds: IBasketBuildL4[];
  total: number;
  basketCreatorUuid?: string;
  travelAgentUserUuid?: string;
}

/**
 * Infinite query hook for Mode 1 (filter-based basket browsing with "Load More" pagination).
 * Each page fetches a batch of builds at a given offset, enriched through L1 → L2 → L4.
 * After mutations (delete, commission, etc.), call invalidateQueries({ queryKey: ['basket'] })
 * and all loaded pages will be refetched automatically.
 */
export const useBasketInfinite = (params: {
  travelAgentUuid?: string | null;
  actingOnBehalfOfUserUuid?: string | null;
  isTa?: boolean;
  actingOnBehalfOfUser?: any;
  enabled?: boolean;
}) => {
  const backendApi = makeBackendApi();

  const isMode1Enabled = !!params.actingOnBehalfOfUserUuid && (params.isTa || !!params.travelAgentUuid);
  const enabled = params.enabled !== false && isMode1Enabled;

  return useInfiniteQuery<IBasketPageData>({
    queryKey: [
      'basket',
      'byFilters',
      params.travelAgentUuid ?? null,
      params.actingOnBehalfOfUserUuid ?? null,
    ],
    queryFn: async ({ pageParam = 0 }): Promise<IBasketPageData> => {
      const offset = typeof pageParam === 'number' ? pageParam : 0;

      // L1: Fetch basket page
      const response = await backendApi.getBasket({
        offset,
        travelAgentUuid: params.isTa ? undefined : params.travelAgentUuid ?? undefined,
        actingOnBehalfOfUserUuid: params.isTa ? undefined : params.actingOnBehalfOfUserUuid ?? undefined,
      });

      // L2: Enrich with hotel photos
      const buildsLevel2 = await getLevel2BasketBuilds({
        builds: response.data?.builds ?? [],
        backendApi,
      });

      // L4: Enrich with booking builder responses
      const effectiveTaUuid = params.travelAgentUuid;

      if (!effectiveTaUuid) {
        return {
          builds: buildsLevel2.map(b => ({ ...b, latestBookingBuilderResponse: undefined })),
          total: response.data.total,
          basketCreatorUuid: response.data.basketCreatorUuid,
          travelAgentUserUuid: response.data.travelAgentUserUuid,
        };
      }

      const responses = await Promise.allSettled(
        buildsLevel2.map(build =>
          backendApi.postBookingBuilderRequest({
            bookingBuilderRequest: build.buildRequest,
            clientCountryCode: build.clientCountryCode,
            travelAgentUuid: effectiveTaUuid,
            validateStatusFunc: status => (status >= 200 && status <= 299) || (status >= 400 && status <= 499),
            actingOnBehalfOfUser: params.actingOnBehalfOfUser ?? undefined,
            queryParams: {
              isForBookingBreakdown: true,
              basketBuildUuid: build.uuid,
            },
          })
        )
      );

      const basketBuildsWithBookingBuilderResponses: IBasketBuildL4[] = buildsLevel2.map(
        (build, index): IBasketBuildL4 => {
          const res = responses[index];
          if (res && res.status === 'fulfilled' && res.value) {
            return {
              ...build,
              latestBookingBuilderResponse: (res.value.data as any).data,
            };
          }
          return {
            ...build,
            latestBookingBuilderResponse: undefined,
          };
        }
      );

      return {
        builds: basketBuildsWithBookingBuilderResponses,
        total: response.data.total,
        basketCreatorUuid: response.data.basketCreatorUuid,
        travelAgentUserUuid: response.data.travelAgentUserUuid,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.flatMap(p => p.builds).length;
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled,
  });
};
